import { ApplicationUserResponse, Claim, CreateUserRequest, DefaultRoles, DeleteUserRolesRequest, UsersClient } from "@api/apiClient";
import axios from "@api/axiosInstance";
import { queryClient } from "@hooks/useGlobalState";
import { useMutation, useQuery } from "@tanstack/react-query";

const usersClient = new UsersClient(undefined, axios);

export const useUser = (userId: string, options?: any) => {
  return useQuery<ApplicationUserResponse>({
    ...options,
    queryKey: ["user", userId],
    initialData: null,
    queryFn: async () => usersClient.getUser(userId),
  });
};

export const useUsers = (role?: DefaultRoles, options?: any) => {
  return useQuery<ApplicationUserResponse[]>({
    ...options,
    queryKey: ["users"],
    queryFn: async () => usersClient.getUsers(role),
    placeholderData: [],
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 60 minutes     
  });
};

export const useUserPermissions = (userId: string | null | undefined, options?: any) => {
  return useQuery<Claim[] | null>({
    ...options,
    queryKey: ["userPermissions", userId],
    initialData: null,
    queryFn: async () => userId ? usersClient.getUserPermissions(userId) : new Promise((resolve) => resolve(null)),
  });
};

export const useCreateUser = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (createUserRequest: CreateUserRequest) => usersClient.createUser(createUserRequest),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["users"], exact: false });
      options?.onSuccess?.();
    },
  });
};

export const useDeleteUser = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (userId: string) => usersClient.deleteUser(userId),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["users"], exact: false });
      options?.onSuccess?.();
    },
  });
};

export const useDeleteUserRoles = (options?: any) => {
  return useMutation<void>({
    ...options,
    mutationFn: (deleteUserRolesRequest: DeleteUserRolesRequest) => usersClient.deleteUserRoles(deleteUserRolesRequest),
    onSuccess: async () => {
      await queryClient.resetQueries({ queryKey: ["users"], exact: false });
      options?.onSuccess?.();
    },
  });
};

